import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-v6';
import {
  Col,
  Button,
  Card,
  Typography,
  message,
  Skeleton,
  Tag,
  List,
} from 'antd';

import useLoading from 'hooks/use-loading';
import useDecodeToken from 'hooks/use-decode-token';

import {
  getSubscription,
  cancelSubscription,
  manageSubscription,
} from 'api-services/user-plan';
import AuthService from 'api-services/auth-service';

import { FREE_PLAN_LOOKUP } from 'utilities/constants';
import eventEmitter from 'utilities/event-emitter';
import { ListIcon } from '../../assets/icons';
import Helper from 'utilities/helper';
import CalenderIcon from 'assets/icons/CalenderIcon';

function UserPlan() {
  const { Title, Text } = Typography;
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { userId, planId } = useDecodeToken();

  const [plan, setPlan] = useState(null);
  const [skeleton, setSkeleton] = useState(true);

  useEffect(() => {
    //handling on change of plan id
    const getActiveSub = async () => {
      setSkeleton(true);
      try {
        const response = await getSubscription(userId); //getting details for active plan
        if (response.status === 200) {
          setPlan(response.data);
          setSkeleton(false);
        }
      } catch (e) {
        console.log(e);
      }
    };

    if (planId) {
      //checking if plan even exists
      getActiveSub();
    }
  }, [planId]);

  /* Plan cancellation handling */
  const handleCancelPlan = async () => {
    if (plan && plan.lookupKey === FREE_PLAN_LOOKUP) {
      //free plan handling
      navigate('/pricing', { state: { hideSideBar: false, upgrade: true } });
      return;
    }

    /* Paid Subscription Cancellation */
    try {
      setLoading(true);

      /* Cancelling plan on backend*/
      const response = await cancelSubscription(userId);
      if (response.status === 200) {
        //positive response handling
        message.success(
          'We have received your subscription cancellation. If you ever decide to return, we will be here to welcome you back.'
        );

        const token = await AuthService.refreshToken(); //refreshing token with updated values
        eventEmitter.emit('userInfoUpdated', token);
        navigate('/pricing', { state: { hideSideBar: false } });
      }
    } catch (err) {
      //negative response handling
      let errMessage = 'Something went wrong. Please try again.';
      if (err.response && err.response.data) {
        //getting errors from API
        if (err.response.data.message) {
          errMessage = err.response.data.message;
        } else if (err.response.data.errors) {
          errMessage = err.response.data.errors;
        }
      }
      message.error(errMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Col
      xs={{ span: 24, offset: 0 }}
      sm={{ span: 18, offset: 0 }}
      md={{ span: 14, offset: 0 }}
      lg={{ span: 10, offset: 0 }}
    >
      <Title level={4}>
        <strong>&nbsp;</strong>
      </Title>
      {skeleton && (
        <Card>
          <Skeleton active />
        </Card>
      )}
      {!skeleton && (
        <Card className="current-plan">
          <Title level={2}>Current Plan</Title>
          {plan?.name && <Title level={3}>{plan?.name}</Title>}
          {plan?.plan && <Title level={3}>{plan?.plan}</Title>}
          {plan?.cancellationRequested && (
            <p className="status">
              Status:
              <Tag bordered={false} color="red">
                Cancelled
              </Tag>
            </p>
          )}
          <p className="count">
            Conversation Count:{' '}
            <span>
              <strong>{plan?.consumed} out of {plan.limit}</strong>
            </span>
          </p>
          <List
            dataSource={Helper.getPlanFeatures(planId)}
            renderItem={(item, index) => (
              <List.Item key={index}>
                <ListIcon />
                {item}
              </List.Item>
            )}
          />
          {plan?.term !== 'OTHER' && (
            <Text level={4} className='pricing-text'>
              ${plan?.price} per {plan?.term === 'MONTHLY' ? 'month' : 'year'}
            </Text>
          )}
        
          <p className="cr-status">
            <CalenderIcon /> Your plan will be 
            {plan?.cancellationRequested ? ' cancelled' : ' renewed'} on <br />
            <span>{plan?.renewalDate}. </span>
          </p>
          {plan?.cancellationRequested && (
            <p className="cr-status">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" rx="12" fill="#FEF1F1" />
                <path
                  d="M11.8125 7.99219L7.17188 15.6094C7.125 15.6797 7.125 15.7266 7.125 15.7969C7.125 15.9844 7.26562 16.125 7.45312 16.125H16.5234C16.7109 16.125 16.875 15.9844 16.875 15.7969C16.875 15.7266 16.8516 15.6797 16.8047 15.6094L12.1641 7.99219C12.1406 7.92188 12.0703 7.875 12 7.875C11.9062 7.875 11.8594 7.92188 11.8125 7.99219ZM10.8516 7.40625C11.0859 7.00781 11.5312 6.75 12 6.75C12.4453 6.75 12.8906 7.00781 13.125 7.40625L17.7656 15.0234C17.9062 15.2578 18 15.5156 18 15.7969C18 16.5938 17.3438 17.25 16.5234 17.25H7.45312C6.65625 17.25 6 16.5938 6 15.7969C6 15.5156 6.07031 15.2578 6.21094 15.0234L10.8516 7.40625ZM12.75 14.625C12.75 15.0469 12.3984 15.375 12 15.375C11.5781 15.375 11.25 15.0469 11.25 14.625C11.25 14.2266 11.5781 13.875 12 13.875C12.3984 13.875 12.75 14.2266 12.75 14.625ZM12.5625 10.3125V12.5625C12.5625 12.8906 12.3047 13.125 12 13.125C11.6719 13.125 11.4375 12.8906 11.4375 12.5625V10.3125C11.4375 10.0078 11.6719 9.75 12 9.75C12.3047 9.75 12.5625 10.0078 12.5625 10.3125Z"
                  fill="#F32735"
                />
              </svg>
              Warning: Cancelling your subscription will delete your chatbots.
            </p>
          )}

          <div className="account-card-footer">
            {plan && plan.lookupKey === FREE_PLAN_LOOKUP ? (
              <Button type="primary" block onClick={handleCancelPlan}>
                Upgrade Plan
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  block
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const response = await manageSubscription(userId);
                      if (response) {
                        window.location.replace(response.url);
                      }
                    } catch (err) {
                      message.error('Something went wrong. Please try again.');
                      setLoading(false);
                    }
                  }}
                >
                  {plan?.cancellationRequested
                    ? 'Renew Subscription'
                    : 'Manage Subscription'}
                </Button>
              </>
            )}
          </div>
        </Card>
      )}
    </Col>
  );
}

export default UserPlan;
